import { FC, memo, useMemo } from 'react';
import { useController } from 'react-hook-form';

import FormControl from '@material-ui/core/FormControl';
import { Skeleton } from '@material-ui/lab';

import { HelperTxt } from '../helperTxt/HelperTxt';
import { TextArea } from '../textArea/TextArea';
import { FormTextAreaProps } from './FormItems.types';

export const FormTextArea: FC<FormTextAreaProps> = memo(
  ({
    isShowRequireTip = true,
    control,
    name,
    size = 'medium',
    variant = 'outlined',
    required,
    rowsMax,
    loading = false,
    ...rest
  }) => {
    const {
      field,
      fieldState: { error },
    } = useController({
      name,
      control,
      defaultValue: '',
    });

    const helperText = useMemo(() => {
      if (error) {
        return error.message;
      }

      if (required && isShowRequireTip) {
        return '*required';
      }

      return '';
    }, [error, required, isShowRequireTip]);

    // Show skeleton when loading
    if (loading) {
      // Calculate height based on rows (default 4 rows for textarea)
      const rows = Number(rest.rows || 4);
      const rowHeight = size === 'small' ? 32 : size === 'large' ? 40 : 36;
      const skeletonHeight = rows * rowHeight + 16; // Add padding

      return (
        <FormControl fullWidth>
          <Skeleton variant="rect" height={skeletonHeight} />
          {required && isShowRequireTip && <Skeleton variant="text" width={80} height={16} style={{ marginTop: 4 }} />}
        </FormControl>
      );
    }

    return (
      <FormControl error={!!error} fullWidth>
        <TextArea
          rowsMax={rowsMax}
          {...rest}
          {...field}
          variant={variant}
          size={size}
          required={required}
          error={!!error}
          InputLabelProps={{ required: false }}
        />

        {helperText && <HelperTxt>{helperText}</HelperTxt>}
      </FormControl>
    );
  },
);
